import { CommonModule } from '@angular/common';
import { Component, inject } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatIconModule } from '@angular/material/icon';
import { Router } from '@angular/router';
import { Environment, EnvironmentService } from '@galaxy/core';
import { COMPANY_PROFILE_CARD_DATA_TOKEN } from '@galaxy/crm/static';
import { PartnerHealthsService } from '@galaxy/partner';
import { TranslateModule } from '@ngx-translate/core';
import { GalaxyButtonLoadingIndicatorModule } from '@vendasta/galaxy/button-loading-indicator';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';
import { PartnerHealthState, PartnerHealthSubState, PartnerModelApiService } from '@vendasta/partner';
import { Observable, combineLatest, firstValueFrom, of, switchMap } from 'rxjs';
import { filter, map, take } from 'rxjs/operators';
import { CompanyAccountService } from '../../../company-account.service';

interface PartnerInformation {
  partnerId: string;
  subscriptionTierId: string;
  health: {
    state: PartnerHealthState;
    subState: PartnerHealthSubState;
    displayState: string;
    displaySubState: string;
    iconCss: string;
  };
}

const companyPartnerFieldId = 'FieldID-6f2472c8-3401-4797-9db1-90fe524bd9a8';

@Component({
  selector: 'app-partner-info',
  standalone: true,
  imports: [
    CommonModule,
    MatExpansionModule,
    MatButtonModule,
    MatIconModule,
    GalaxyButtonLoadingIndicatorModule,
    TranslateModule,
  ],
  templateUrl: './partner-info.component.html',
  styleUrls: ['../association-panel-base-styles.component.scss', './partner-info.component.scss'],
})
export class PartnerInfoComponent {
  private readonly data = inject(COMPANY_PROFILE_CARD_DATA_TOKEN);
  protected companyPartnerInfo$: Observable<PartnerInformation>;
  protected createPartnerButtonLoading$ = this.accountCreationService.createAccountGroupLoading$(this.data.company_id);

  constructor(
    private readonly partnerModelService: PartnerModelApiService,
    private router: Router,
    private readonly accountCreationService: CompanyAccountService,
    private readonly partnerHealthsService: PartnerHealthsService,
    private environmentService: EnvironmentService,
    private readonly snackbarService: SnackbarService,
  ) {
    this.setupCompanyInfoFetching();
  }

  private setupCompanyInfoFetching(): void {
    const company$ = this.accountCreationService.getCompany$(this.data.company_id);

    const companyPID$ = company$.pipe(
      map((company) => {
        return company.fields?.find((field) => field.fieldId === companyPartnerFieldId)?.stringValue;
      }),
    );

    const subscriptionTierId$ = companyPID$.pipe(
      switchMap((pid) => {
        if (!pid) {
          return of(null);
        }
        return this.partnerModelService.getPartnerModel({ partnerId: pid });
      }),
      map((resp) => {
        return resp?.partner?.subscriptionTierId;
      }),
    );

    const financialHealth$ = companyPID$.pipe(
      switchMap((pid) => {
        if (!pid) {
          return of(null);
        }
        return this.partnerHealthsService.get(pid);
      }),
      map((resp) => {
        return resp;
      }),
    );

    this.companyPartnerInfo$ = combineLatest([companyPID$, subscriptionTierId$, financialHealth$]).pipe(
      filter(([pid, tierId]) => !!pid && !!tierId),
      map(([pid, tierId, health]) => {
        const display = this.displayTextAndIcon(health);
        return <PartnerInformation>{
          partnerId: pid,
          subscriptionTierId: tierId,
          health: {
            state: health?.state,
            subState: health?.subState,
            displayState: display[0],
            displaySubState: display[2],
            iconCss: display[1],
          },
        };
      }),
    );
  }

  createPartner(): void {
    if (!this.data.company_id) {
      this.snackbarService.openErrorSnack('CRM.ERROR.NO_COMPANY_ID');
      return;
    }

    firstValueFrom(this.accountCreationService.fetchAccountGroupId$(this.data.company_id))
      .then((accountGroupId) => {
        if (accountGroupId) {
          this.router.navigateByUrl(`/superadmin/partner/create?fromAccountGroup=${accountGroupId}`);
        } else {
          this.snackbarService.openErrorSnack('CRM.ERROR.NAVIGATE_TO_PAGE_FAILED');
        }
      })
      .catch((err) => {
        console.error(err);
        this.snackbarService.openErrorSnack('CRM.ERROR.NAVIGATE_TO_PAGE_FAILED');
      });
  }

  private displayTextAndIcon(health): string[] {
    let subStateDisplay = '';
    switch (health?.subState) {
      case 0:
        subStateDisplay = 'CRM.ASSOCIATION_PANEL.PARTNER_INFORMATION.HEALTH_STATE.UNSPECIFIED';
        break;
      case 1:
        subStateDisplay = 'CRM.ASSOCIATION_PANEL.PARTNER_INFORMATION.HEALTH_STATE.ACTIVE';
        break;
      case 2:
        subStateDisplay = 'CRM.ASSOCIATION_PANEL.PARTNER_INFORMATION.HEALTH_STATE.OVERDUE';
        break;
      case 3:
        subStateDisplay = 'CRM.ASSOCIATION_PANEL.PARTNER_INFORMATION.HEALTH_STATE.SUSPENDED';
        break;
      case 4:
        subStateDisplay = 'CRM.ASSOCIATION_PANEL.PARTNER_INFORMATION.HEALTH_STATE.PAYMENT_PLAN';
        break;
      default:
        subStateDisplay = 'CRM.ASSOCIATION_PANEL.PARTNER_INFORMATION.HEALTH_STATE.UNSPECIFIED';
        break;
    }

    switch (health?.state) {
      case 0:
        return [
          'CRM.ASSOCIATION_PANEL.PARTNER_INFORMATION.HEALTH_STATE.HEALTHY',
          'partner-health-token-healthy',
          subStateDisplay,
        ];
      case 1:
        return [
          'CRM.ASSOCIATION_PANEL.PARTNER_INFORMATION.HEALTH_STATE.AT_RISK',
          'partner-health-token-risk',
          subStateDisplay,
        ];
      case 2:
        return [
          'CRM.ASSOCIATION_PANEL.PARTNER_INFORMATION.HEALTH_STATE.RESTRICTED',
          'partner-health-token-restricted',
          subStateDisplay,
        ];
      default:
        return [
          'CRM.ASSOCIATION_PANEL.PARTNER_INFORMATION.HEALTH_STATE.HEALTHY',
          'partner-health-token-healthy',
          subStateDisplay,
        ];
    }
  }

  goToPricing(): void {
    this.companyPartnerInfo$
      .pipe(
        take(1),
        map((partnerInfo) => {
          let url = '';
          if (this.environmentService.getEnvironment() === Environment.PROD) {
            url = 'https://billing-prod.apigateway.co/merchants/' + partnerInfo.partnerId;
          } else {
            url = 'https://billing-demo.apigateway.co/merchants/' + partnerInfo.partnerId;
          }
          window.open(url, '_blank');
        }),
      )
      .subscribe();
  }
}
